import React, { useState, useEffect } from "react";
import { apiService } from "../services/api";

interface TwoFactorSetupProps {
  onSetupComplete: () => void;
  onCancel: () => void;
}

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  onSetupComplete,
  onCancel,
}) => {
  const [step, setStep] = useState<"password" | "scan" | "verify">("password");
  const [password, setPassword] = useState("");
  const [totpToken, setTotpToken] = useState("");
  const [secret, setSecret] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [backupUrl, setBackupUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);

  useEffect(() => {
    checkTwoFAStatus();
    // Try password-less setup first (for users who just logged in)
    attemptPasswordlessSetup();
  }, []);

  const checkTwoFAStatus = async () => {
    try {
      const status = await apiService.getTwoFAStatus();
      setTwoFAEnabled(status.enabled);
    } catch (err) {
      console.error("Failed to check 2FA status:", err);
    }
  };

  const attemptPasswordlessSetup = async () => {
    // Try to setup 2FA without password (works if user just logged in)
    try {
      const response = await apiService.setupTwoFA("");
      // Success! User just logged in, no password needed
      setSecret(response.secret);
      setQrCode(response.qr_code);
      setBackupUrl(response.backup_url);
      setStep("scan");
    } catch (err) {
      // Password required - user needs to enter it
      // This is expected if session is older than 2 minutes
      console.debug("Password required for 2FA setup:", err);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.setupTwoFA(password);
      setSecret(response.secret);
      setQrCode(response.qr_code);
      setBackupUrl(response.backup_url);
      setStep("scan");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to setup 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await apiService.enableTwoFA(totpToken, secret);
      onSetupComplete();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Invalid verification code",
      );
    } finally {
      setLoading(false);
    }
  };

  if (twoFAEnabled) {
    return (
      <div style={{ maxWidth: "600px", margin: "50px auto", padding: "20px" }}>
        <h2>Two-Factor Authentication</h2>
        <div
          style={{
            padding: "15px",
            backgroundColor: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "4px",
            marginBottom: "20px",
          }}
        >
          <strong>Two-factor authentication is enabled</strong>
          <p style={{ marginTop: "10px", marginBottom: "0" }}>
            Your account is protected with two-factor authentication.
          </p>
        </div>
        <button
          onClick={onCancel}
          style={{
            padding: "10px 20px",
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "600px", margin: "50px auto", padding: "20px" }}>
      <h2>Setup Two-Factor Authentication</h2>

      {step === "password" && (
        <form onSubmit={handlePasswordSubmit}>
          <div style={{ marginBottom: "20px" }}>
            <p>
              To enable two-factor authentication, please verify your password
              first.
            </p>
          </div>
          <div style={{ marginBottom: "15px" }}>
            <label htmlFor="password">Current Password:</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "8px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </div>
          {error && (
            <div style={{ color: "red", marginBottom: "15px" }}>{error}</div>
          )}
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Verifying..." : "Continue"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: "10px 20px",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {step === "scan" && (
        <div>
          <div style={{ marginBottom: "20px" }}>
            <p>
              <strong>Step 1:</strong> Scan this QR code with your authenticator
              app (Google Authenticator, Authy, etc.)
            </p>
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                marginTop: "15px",
              }}
            >
              <img
                src={qrCode}
                alt="2FA QR Code"
                style={{ maxWidth: "300px" }}
              />
            </div>
          </div>

          <div
            style={{
              marginBottom: "20px",
              padding: "15px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "4px",
            }}
          >
            <p>
              <strong>Can't scan the QR code?</strong>
            </p>
            <p style={{ fontSize: "14px", marginTop: "10px" }}>
              Manual entry code:{" "}
              <code
                style={{
                  padding: "2px 6px",
                  backgroundColor: "#e9ecef",
                  borderRadius: "3px",
                  fontSize: "12px",
                }}
              >
                {secret}
              </code>
            </p>
          </div>

          <button
            onClick={() => setStep("verify")}
            style={{
              padding: "10px 20px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            I've scanned the code
          </button>
        </div>
      )}

      {step === "verify" && (
        <form onSubmit={handleVerifyToken}>
          <div style={{ marginBottom: "20px" }}>
            <p>
              <strong>Step 2:</strong> Enter the 6-digit code from your
              authenticator app
            </p>
          </div>
          <div style={{ marginBottom: "15px" }}>
            <label htmlFor="totpToken">Verification Code:</label>
            <input
              type="text"
              id="totpToken"
              value={totpToken}
              onChange={(e) =>
                setTotpToken(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              required
              maxLength={6}
              placeholder="000000"
              style={{
                width: "100%",
                padding: "12px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "18px",
                letterSpacing: "0.5em",
                textAlign: "center",
              }}
            />
          </div>
          {error && (
            <div style={{ color: "red", marginBottom: "15px" }}>{error}</div>
          )}
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="submit"
              disabled={loading || totpToken.length !== 6}
              style={{
                padding: "10px 20px",
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor:
                  loading || totpToken.length !== 6 ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Verifying..." : "Enable 2FA"}
            </button>
            <button
              type="button"
              onClick={() => setStep("scan")}
              style={{
                padding: "10px 20px",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Back
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
